/**
 * AI Agent service layer.
 * ---------------------------------------------------------------------------
 * This module is the SINGLE integration point between the AI Agent UI and the
 * backend. The UI never talks to HTTP directly - it only consumes the typed
 * functions exported here.
 *
 * It calls the CodeNekt API (cdnktapi) at POST /ai-agent/chat[/stream], using
 * the same authenticated axios instance as the rest of the app (the
 * X-token-api header is attached automatically by src/api/axios.ts's
 * interceptor). The backend resolves which company this conversation is
 * scoped to from that token's session - the frontend sends the active fleet
 * id only as a CLAIM that the backend verifies, and never talks to the AI
 * agent service directly (it doesn't even know that service exists). This is
 * what keeps the multi-tenant fleet data isolated between customers.
 */
import axios, { currentLanguage } from "./axios";
import { getCurrentFleetId, isFleetAdmin } from "../utils/getCurrentFleet";

// --- Configuration ----------------------------------------------------------
export const AI_AGENT_CONFIG: {
  /** Endpoint on the CodeNekt API (cdnktapi), not the AI agent directly. */
  endpoint: string;
  /** SSE endpoint - live tool progress + final answer as they happen. */
  streamEndpoint: string;
  /** Cadence (ms) used to reveal the final answer word-by-word. */
  revealLatencyMs: number;
  model: string;
} = {
  endpoint: "/ai-agent/chat",
  streamEndpoint: "/ai-agent/chat/stream",
  revealLatencyMs: 18,
  model: "codenekt-fleet-agent",
};

// --- Domain types -------------------------------------------------------------
export type Role = "user" | "assistant" | "system";

export type ToolStatus = "pending" | "running" | "succeeded" | "failed";

export interface ToolInvocation {
  id: string;
  name: string;
  /** Human label shown in the trace, e.g. "Querying fleet vehicles". */
  label: string;
  status: ToolStatus;
  /** Optional short result summary surfaced in the trace. */
  result?: string;
}

export interface Citation {
  id: string;
  title: string;
  /** In-app deep link (route) the citation points at. */
  href?: string;
}

export interface ChatMessage {
  id: string;
  role: Role;
  content: string;
  createdAt: number;
  /** Present on assistant messages that used tools. */
  tools?: ToolInvocation[];
  citations?: Citation[];
  /** True while tokens are still streaming in. */
  streaming?: boolean;
  /** Set when the turn failed so the UI can offer a retry. */
  error?: boolean;
}

/** Agent operating modes exposed by the backend (POST /ai-agent/chat `mode`). */
export type AgentMode = "manager" | "driver" | "autopilot" | "guide";

export const AGENT_MODES: { id: AgentMode; label: string; icon: string }[] = [
  { id: "manager", label: "Manager", icon: "chart" },
  { id: "driver", label: "Driver", icon: "vehicule" },
  { id: "autopilot", label: "Autopilot", icon: "stars" },
];

/** Modes an EMPLOYEE may use. Manager and Autopilot answer for the WHOLE
 * fleet, so they are restricted to FLEET_MANAGER. */
const EMPLOYEE_MODES: AgentMode[] = ["driver", "guide"];

/**
 * The modes this user is actually allowed to pick.
 *
 * This is a UX filter, NOT the security boundary: the API resolves the
 * caller's company_role from their session and returns 403 for a mode
 * their role does not allow, regardless of what the client sends. Filtering
 * here just stops us rendering a chip that can only ever fail.
 */
export const availableAgentModes = (): { id: AgentMode; label: string; icon: string }[] =>
  isFleetAdmin()
    ? AGENT_MODES
    : AGENT_MODES.filter((m) => EMPLOYEE_MODES.includes(m.id));

/** Default mode for this user. An employee must not start in manager mode:
 * every turn would 403. */
export const defaultAgentMode = (): AgentMode =>
  isFleetAdmin() ? "manager" : "driver";

export interface Conversation {
  id: string;
  title: string;
  createdAt: number;
  updatedAt: number;
  messages: ChatMessage[];
  /**
   * The AI agent's own session id. Sent back on every follow-up turn so the
   * agent keeps its conversation memory. Unset until the first reply.
   */
  sessionId?: string;
  /** Mode sent to the agent: manager | driver | autopilot. */
  agentMode?: string;
}

export interface AgentCapability {
  id: string;
  icon: string; // Icon name resolved by <Icon />
  /** i18n keys - translated at RENDER time so the hero follows live
   * language switches (never bake i18n.t() into module-level constants). */
  titleKey: string;
  titleDefault: string;
  descriptionKey: string;
  descriptionDefault: string;
}

export interface SuggestedPrompt {
  id: string;
  icon: string;
  labelKey: string;
  labelDefault: string;
  promptKey: string;
  promptDefault: string;
}

// --- Static metadata (safe to keep client-side) --------------------------------
export const AGENT_CAPABILITIES: AgentCapability[] = [
  {
    id: "fleet-insights",
    icon: "vehicule",
    titleKey: "aiAgent.caps.fleetTitle",
    titleDefault: "Fleet insights",
    descriptionKey: "aiAgent.caps.fleetDesc",
    descriptionDefault:
      "Ask about utilization, energy mix, costs and status across your whole fleet.",
  },
  {
    id: "documents",
    icon: "fileStack",
    titleKey: "aiAgent.caps.docsTitle",
    titleDefault: "Documents & compliance",
    descriptionKey: "aiAgent.caps.docsDesc",
    descriptionDefault:
      "Surface expiring contracts, missing documents and registration renewals.",
  },
  {
    id: "claims",
    icon: "sinistre",
    titleKey: "aiAgent.caps.claimsTitle",
    titleDefault: "Claims assistant",
    descriptionKey: "aiAgent.caps.claimsDesc",
    descriptionDefault:
      "Summarize open claims, draft follow-ups and flag what needs attention.",
  },
  {
    id: "actions",
    icon: "stars",
    titleKey: "aiAgent.caps.actionsTitle",
    titleDefault: "Guided actions",
    descriptionKey: "aiAgent.caps.actionsDesc",
    descriptionDefault:
      "Get step-by-step help to add a vehicle, invite a collaborator or assign a card.",
  },
];

export const SUGGESTED_PROMPTS: SuggestedPrompt[] = [
  {
    id: "p-energy",
    icon: "eco",
    labelKey: "aiAgent.prompts.energy",
    labelDefault: "Energy mix breakdown",
    promptKey: "aiAgent.prompts.energyPrompt",
    promptDefault: "What's the current energy mix of my fleet and how is it trending?",
  },
  {
    id: "p-expiring",
    icon: "license",
    labelKey: "aiAgent.prompts.expiring",
    labelDefault: "Expiring contracts",
    promptKey: "aiAgent.prompts.expiringPrompt",
    promptDefault: "Which vehicle contracts are ending in the next 60 days?",
  },
  {
    id: "p-claims",
    icon: "sinistre",
    labelKey: "aiAgent.prompts.claims",
    labelDefault: "Open claims summary",
    promptKey: "aiAgent.prompts.claimsPrompt",
    promptDefault: "Summarize my open claims and tell me which ones need action.",
  },
  {
    id: "p-cost",
    icon: "currency",
    labelKey: "aiAgent.prompts.cost",
    labelDefault: "Cost optimization",
    promptKey: "aiAgent.prompts.costPrompt",
    promptDefault: "Where could I reduce total cost of ownership across the fleet?",
  },
];

// --- Utilities ------------------------------------------------------------------
const uid = (): string =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `id-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const STORAGE_KEY = "cdnkt.ai.conversations.v1";

/** Only forward a real, positive fleet id - localStorage can hold
 * stale/partial currentUser data, and 0/undefined must never be sent
 * as a fleet claim. The backend verifies membership either way. */
const claimedFleetId = (): number | undefined => {
  const raw = getCurrentFleetId();
  return typeof raw === "number" && raw > 0 ? raw : undefined;
};

// --- Local persistence -------------------------------------------------------
export const loadConversations = (): Conversation[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as Conversation[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const persistConversations = (conversations: Conversation[]): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(conversations));
  } catch {
    /* storage full / unavailable - non-fatal for the UI */
  }
};

export const makeConversation = (title?: string): Conversation => {
  const now = Date.now();
  return {
    id: uid(),
    title: title ?? "New conversation",
    createdAt: now,
    updatedAt: now,
    messages: [],
  };
};

export const deriveTitle = (firstUserMessage: string): string => {
  const clean = firstUserMessage.trim().replace(/\s+/g, " ");
  return clean.length > 48 ? `${clean.slice(0, 48)}…` : clean || "New conversation";
};

// --- Public streaming API ------------------------------------------------------
export interface StreamHandlers {
  onToolUpdate?: (tools: ToolInvocation[]) => void;
  onToken?: (chunk: string) => void;
  onDone?: (message: ChatMessage) => void;
  onError?: (err: Error) => void;
  /** Fired once the agent hands back a session id, so the caller can
   * persist it onto the Conversation for the next turn. */
  onSessionId?: (sessionId: string) => void;
  signal?: AbortSignal;
}

interface LiveChatResponse {
  answer: string;
  session_id: string;
  mode: string;
  thinking_steps: { tool: string; input: string; observation: string }[];
  timestamp: string;
}

/** Reveal `answer` through onToken word-by-word so the reply reads
 * naturally even when it arrives as a single payload. */
const replayAsStream = async (
  answer: string,
  handlers: Pick<StreamHandlers, "onToken" | "signal">
): Promise<string> => {
  const { onToken, signal } = handlers;
  const words = answer.split(/(\s+)/);
  let acc = "";
  for (const w of words) {
    if (signal?.aborted) return acc;
    acc += w;
    onToken?.(w);
    await sleep(AI_AGENT_CONFIG.revealLatencyMs);
  }
  return acc;
};

/**
 * Free the agent-side session (conversation memory) for a deleted
 * conversation. Fire-and-forget: local deletion must never be blocked by
 * a backend hiccup, so callers should not await UI on this.
 */
export const resetAgentSession = async (sessionId: string): Promise<void> => {
  try {
    await axios.post("/ai-agent/session/reset", { session_id: sessionId });
  } catch {
    /* non-fatal: the agent's TTL eviction will reclaim it eventually */
  }
};

/**
 * Streaming transport: consumes the SSE endpoint so the user sees the
 * agent's tool steps LIVE (each step appears the moment it starts/finishes)
 * instead of after the whole turn is over.
 *
 * Server event contract (JSON on "data:" lines):
 *   {event:"session", session_id}
 *   {event:"thinking"}
 *   {event:"step_start", tool, input} / {event:"step_end", observation|error}
 *   {event:"answer", answer, thinking_steps}
 *   {event:"error", message} / {event:"done"}
 */
const liveAgentReplyStream = async (
  prompt: string,
  handlers: StreamHandlers,
  sessionId: string | undefined,
  mode: string
): Promise<void> => {
  const { onToolUpdate, onDone, onSessionId, onError, signal } = handlers;

  const base = (process.env.REACT_APP_BASE_URL || "").replace(/\/$/, "");
  const token = localStorage.getItem("token") || "";

  /*
    Cette requete est un `fetch` brut (le SSE ne passe pas par axios), donc
    l'intercepteur qui ajoute `?lang=` sur tous les autres appels ne
    s'applique PAS ici. Sans cet en-tete, la route de streaming - qui est le
    transport principal de l'assistant - arrivait a l'API sans aucune
    information de langue : SetLocale retombait sur la preference du compte
    ou sur le defaut, et l'agent repondait en francais a un utilisateur qui
    venait de basculer l'interface en allemand.

    En-tete plutot que parametre d'URL : `X-App-Language` est deja l'une des
    sources declarees par config('locales.sources') cote API, et un POST n'a
    pas de raison de porter sa langue dans la query string.
  */
  const lang = currentLanguage();

  const res = await fetch(`${base}${AI_AGENT_CONFIG.streamEndpoint}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      "X-token-api": token,
      ...(lang ? { "X-App-Language": lang } : {}),
    },
    body: JSON.stringify({
      message: prompt,
      session_id: sessionId,
      mode,
      fleet_id: claimedFleetId(),
    }),
    signal,
  });

  if (!res.ok || !res.body) {
    throw new Error(`stream unavailable (${res.status})`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  const tools: ToolInvocation[] = [];
  let finalAnswer: string | null = null;
  let serverError: string | null = null;

  // Internal pseudo-tools the agent runs as part of its own retry loop.
  // "_Exception" is LangChain's ExceptionTool, used to hand a format
  // correction back to the model; its "result" is prompt text telling the
  // model off, which reads as a crash to the user. The agent filters these
  // out now, but keep the client honest too: an older agent build must not
  // be able to put them back on screen.
  const isInternalTool = (name?: string) =>
    typeof name === "string" && name.startsWith("_");

  /** Tool results are Markdown written for the model. Flatten them for the
   * one-line trace chip instead of showing raw "##" and "**" markers. */
  const previewObservation = (raw: unknown) =>
    String(raw ?? "")
      .replace(/\[(?:SYSTEME|RESULTAT DEFINITIF)[^\]]*\]/g, "")
      .replace(/[*`#|]+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 160);

  const handleEvent = (payload: any) => {
    switch (payload.event) {
      case "session":
        if (payload.session_id) onSessionId?.(payload.session_id);
        break;
      case "step_start":
        if (isInternalTool(payload.tool)) break;
        tools.push({
          id: uid(),
          name: payload.tool,
          label: payload.tool,
          status: "running",
        });
        onToolUpdate?.([...tools]);
        break;
      case "step_end": {
        const last = [...tools].reverse().find((t) => t.status === "running");
        if (last) {
          last.status = payload.error ? "failed" : "succeeded";
          last.result = payload.error
            ? String(payload.error)
            : previewObservation(payload.observation);
          onToolUpdate?.([...tools]);
        }
        break;
      }
      case "step_drop": {
        // The agent re-issued a call the backend loop guard short-circuited.
        // Nothing was executed and there is no result to show, so withdraw
        // the pending row instead of leaving a spinner or rendering an
        // internal directive as if it were fleet data.
        const idx = [...tools]
          .map((t, i) => ({ t, i }))
          .reverse()
          .find(({ t }) => t.status === "running")?.i;
        if (idx !== undefined) {
          tools.splice(idx, 1);
          onToolUpdate?.([...tools]);
        }
        break;
      }
      case "answer":
        finalAnswer = payload.answer ?? "";
        break;
      case "error":
        serverError = payload.message || "AI agent error";
        break;
      default:
        break; // "thinking", heartbeats - no UI change needed
    }
  };

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let sep: number;
    // SSE frames are separated by a blank line.
    while ((sep = buffer.indexOf("\n\n")) !== -1) {
      const frame = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      for (const line of frame.split("\n")) {
        if (!line.startsWith("data:")) continue; // ignore ": ping" heartbeats
        try {
          handleEvent(JSON.parse(line.slice(5).trim()));
        } catch {
          /* malformed frame - skip */
        }
      }
    }
    if (signal?.aborted) {
      reader.cancel().catch(() => undefined);
      return;
    }
  }

  if (signal?.aborted) return;

  if (serverError && finalAnswer === null) {
    onError?.(new Error(serverError));
    return;
  }

  const acc = await replayAsStream(finalAnswer ?? "", {
    onToken: handlers.onToken,
    signal,
  });

  const message: ChatMessage = {
    id: uid(),
    role: "assistant",
    content: acc,
    createdAt: Date.now(),
    tools: tools.length ? tools : undefined,
    streaming: false,
  };
  onDone?.(message);
};

/** Buffered JSON transport - fallback when the SSE stream cannot be
 * established (older backend, proxy issue, ...). */
const liveAgentReply = async (
  prompt: string,
  handlers: StreamHandlers,
  sessionId: string | undefined,
  mode: string
): Promise<void> => {
  const { onToolUpdate, onDone, onSessionId, signal } = handlers;

  const { data } = await axios.post<LiveChatResponse>(
    AI_AGENT_CONFIG.endpoint,
    {
      message: prompt,
      session_id: sessionId,
      mode,
      fleet_id: claimedFleetId(),
    },
    // Local LLM fallback inference can take minutes; override the
    // instance-wide timeout for this call only (backend/nginx cap the
    // real limit at ~300s).
    { signal, timeout: 320000 }
  );

  if (signal?.aborted) return;

  onSessionId?.(data.session_id);

  const tools: ToolInvocation[] = (data.thinking_steps ?? [])
    .filter((step) => !(step.tool ?? "").startsWith("_"))
    .map((step) => ({
      id: uid(),
      name: step.tool,
      label: step.tool,
      status: "succeeded" as ToolStatus,
      result: String(step.observation ?? "")
        .replace(/[*`#|]+/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 160),
    }));
  if (tools.length) onToolUpdate?.(tools);

  const acc = await replayAsStream(data.answer ?? "", {
    onToken: handlers.onToken,
    signal,
  });

  const message: ChatMessage = {
    id: uid(),
    role: "assistant",
    content: acc,
    createdAt: Date.now(),
    tools: tools.length ? tools : undefined,
    streaming: false,
  };
  onDone?.(message);
};

/**
 * Sends a user turn and streams the assistant response back through handlers.
 * Prefers the SSE transport (live tool progress, early feedback), and
 * transparently falls back to the buffered JSON endpoint.
 */
export const streamAgentReply = async (
  prompt: string,
  _history: ChatMessage[],
  handlers: StreamHandlers,
  sessionId?: string,
  mode: string = defaultAgentMode()
): Promise<void> => {
  const { onError, signal } = handlers;

  try {
    try {
      await liveAgentReplyStream(prompt, handlers, sessionId, mode);
    } catch (streamErr) {
      if (signal?.aborted) return;
      await liveAgentReply(prompt, handlers, sessionId, mode);
    }
  } catch (err) {
    if (signal?.aborted) return;
    onError?.(err instanceof Error ? err : new Error(String(err)));
  }
};
